import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../utils/axios';
import { useAuth } from './AuthContext';

// Create the client context
const ClientContext = createContext();

// Hook for using the context
export const useClient = () => useContext(ClientContext);

/**
 * Provider component for the Client context
 * 
 * @param {object} props - Component props
 * @returns {JSX.Element} Context provider
 */
export const ClientProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  // Load clients when the provider mounts and user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchClients();
      
      // Try to load previously selected client from localStorage
      const savedClientJson = localStorage.getItem('selectedClient');
      if (savedClientJson) {
        try {
          const savedClient = JSON.parse(savedClientJson);
          setSelectedClient(savedClient);
        } catch (err) {
          console.error('Error parsing saved client data:', err);
          localStorage.removeItem('selectedClient');
        }
      }
    }
  }, [isAuthenticated]);

  /**
   * Fetch all clients from the API
   */
  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use v1 API path
      const response = await axios.get('/api/v1/clients');
      setClients(response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.response?.data?.detail || 'Failed to load clients');
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Select a client by ID
   * 
   * @param {number} clientId - The ID of the client to select
   */
  const selectClient = async (clientId) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!clientId) {
        // If no client ID is provided, clear the selected client
        setSelectedClient(null);
        localStorage.removeItem('selectedClient');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('client-changed', {
          detail: { client: null }
        }));
        
        return null;
      }
      
      // Find the client in the current list
      const client = clients.find(c => c.id === clientId);
      
      if (client) {
        // Client is already loaded, just select it
        setSelectedClient(client);
        localStorage.setItem('selectedClient', JSON.stringify(client));
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('client-changed', {
          detail: { client }
        }));
        
        return client;
      }
      
      // If not found, try to fetch from API
      const response = await axios.get(`/api/v1/clients/${clientId}`);
      setSelectedClient(response.data);
      localStorage.setItem('selectedClient', JSON.stringify(response.data));
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('client-changed', {
        detail: { client: response.data }
      }));
      
      return response.data;
    } catch (err) {
      console.error('Error selecting client:', err);
      setError(err.response?.data?.detail || 'Failed to select client');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get client by ID
   * 
   * @param {number} clientId - The ID of the client to get
   * @returns {Promise<object|null>} The client object or null if not found
   */
  const getClient = async (clientId) => {
    try {
      // Check if client is in the current list
      const client = clients.find(c => c.id === clientId);
      if (client) {
        return client;
      }
      
      // If not, fetch from API
      const response = await axios.get(`/api/v1/clients/${clientId}`);
      return response.data;
    } catch (err) {
      console.error(`Error getting client ${clientId}:`, err);
      return null;
    }
  };

  // The value provided to consumers of this context
  const contextValue = {
    clients,
    selectedClient,
    loading,
    error,
    fetchClients,
    selectClient,
    getClient
  };

  return (
    <ClientContext.Provider value={contextValue}>
      {children}
    </ClientContext.Provider>
  );
};

export default ClientProvider;